import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Play, Menu, X, Home, Tag, Blocks, Info, Newspaper, BookOpen, Snowflake, Mail } from 'lucide-react';
import NavbarVisitorPill from './NavbarVisitorPill'; // v34.92 — live visitor count next to Sign In
// LiveBuildPill import removed (Iter84f) — Build-in-Public UI hidden from public nav.

// v35.68 (Jul 2026) — founder wants the same colorful icon treatment as the
// authed mega-menu: each drawer link gets an icon in a vibrant tinted square.
const DRAWER_TINTS = [
  'bg-emerald-500/15 text-emerald-300',
  'bg-blue-500/15 text-blue-300',
  'bg-amber-500/15 text-amber-300',
  'bg-rose-500/15 text-rose-300',
  'bg-cyan-500/15 text-cyan-300',
  'bg-violet-500/15 text-violet-300',
];

const NAV_LINKS = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/pricing', label: 'Pricing', icon: Tag },
  { to: '/integrations', label: 'Integrations', icon: Blocks },
  { to: '/about', label: 'About', icon: Info },
  { to: '/blog', label: 'Blog', icon: Newspaper },
  { to: '/guides', label: 'Guides', icon: BookOpen },
  // 'Live Build' and 'Developers' links removed (Iter84f) — Build-in-Public
  // visibility toggled OFF to reduce competitive surface.
  { to: '/snowflake-health-score', label: 'Free Snowflake Audit', icon: Snowflake },
  { to: '/contact', label: 'Contact', icon: Mail },
];

const PublicNavbar = ({ onDemoClick, demoLoading }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close drawer when navigating
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50 bg-slate-950 backdrop-blur-2xl border-b border-white/[0.06]"
        data-testid="public-navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="public-navbar-logo">
              <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold">
                <span className="text-white">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
              </span>
            </Link>

            {/* Desktop links */}
            <div className="hidden md:flex items-center gap-7">
              <Link to="/" className="text-sm text-slate-400 hover:text-white transition-colors">Home</Link>
              <Link to="/pricing" className="text-sm text-slate-400 hover:text-white transition-colors">Pricing</Link>
              <Link to="/integrations" className="text-sm text-slate-400 hover:text-white transition-colors">Integrations</Link>
              <Link to="/about" className="text-sm text-slate-400 hover:text-white transition-colors">About</Link>
              <Link to="/blog" className="text-sm text-slate-400 hover:text-white transition-colors">Blog</Link>
              <Link to="/guides" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="public-navbar-guides">Guides</Link>
              <Link to="/developers" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="public-navbar-developers">Developers</Link>
              <Link to="/faq" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="public-navbar-faq">FAQ</Link>
              <Link to="/contact" className="text-sm text-slate-400 hover:text-white transition-colors">Contact</Link>
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-2">
              {/* v34.92 — Live visitor pill. Sits before Sign In so every
                  arriving visitor sees the global count tick up.
                  Shows "You're visitor #N" until 100, then flips to
                  "N visitors and growing". Hidden on mobile (lg breakpoint)
                  to avoid cramping the hamburger. */}
              <NavbarVisitorPill />
              {/* Live Build pulse-pill removed (Iter84f) */}
              <Link to="/login" className="hidden md:inline-flex">
                <Button variant="ghost" className="text-slate-300 hover:text-white" data-testid="public-navbar-signin">
                  Sign In
                </Button>
              </Link>
              {onDemoClick ? (
                <Button
                  onClick={onDemoClick}
                  disabled={demoLoading}
                  className="hidden sm:inline-flex bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="nav-demo-btn"
                >
                  <Play className="w-4 h-4 mr-1.5 fill-current" />Live Demo
                </Button>
              ) : (
                <Link to="/login" className="hidden sm:inline-flex">
                  <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">Get Started</Button>
                </Link>
              )}

              {/* Mobile hamburger */}
              <button
                type="button"
                className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                onClick={() => setOpen(true)}
                aria-label="Open menu"
                data-testid="public-navbar-mobile-toggle"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-[60]" data-testid="public-navbar-mobile-drawer">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          {/* Panel */}
          <div className="absolute top-0 right-0 bottom-0 w-[88%] max-w-sm bg-slate-950 border-l border-white/10 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between h-16 px-4 border-b border-white/5">
              <Link to="/" className="flex items-center gap-2" onClick={() => setOpen(false)}>
                <img src="/icons/cartie-emblem.png" alt="" className="w-8 h-8" />
                <span className="font-bold text-white">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
              </Link>
              <button
                type="button"
                className="w-10 h-10 inline-flex items-center justify-center rounded-lg text-slate-300 hover:text-white hover:bg-white/5"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="public-navbar-mobile-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              <ul className="space-y-1">
                {NAV_LINKS.map((l, i) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className={`flex items-center gap-3 px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                        location.pathname === l.to
                          ? 'bg-emerald-500/15 text-emerald-300'
                          : 'text-slate-300 hover:bg-white/5 hover:text-white'
                      }`}
                      onClick={() => setOpen(false)}
                      data-testid={`public-navbar-mobile-link-${l.to.replace(/\//g, '') || 'home'}`}
                    >
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-md shrink-0 ${
                        location.pathname === l.to ? 'bg-emerald-500 text-white' : DRAWER_TINTS[i % DRAWER_TINTS.length]
                      }`}>
                        <l.icon className="w-4 h-4" strokeWidth={2.2} />
                      </span>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="px-4 py-4 border-t border-white/5 space-y-2">
              <Link to="/login" onClick={() => setOpen(false)} className="block">
                <Button variant="outline" className="w-full border-slate-700 text-slate-300 hover:bg-white/5">
                  Sign In
                </Button>
              </Link>
              {onDemoClick ? (
                <Button
                  onClick={() => { onDemoClick(); setOpen(false); }}
                  disabled={demoLoading}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  <Play className="w-4 h-4 mr-1.5 fill-current" />Live Demo
                </Button>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="block">
                  <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
                    Get Started
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PublicNavbar;
